/**
 * Multi Column Cards - Swiper slider initialisation.
 *
 * Activates a Swiper instance only when the block is in slider mode
 * (i.e. when data-card-count > 4, as determined server-side by PHP).
 * In grid mode the element carries the class `mcc-wrap--grid` and this
 * module does nothing, leaving layout entirely to CSS.
 *
 * Arrow buttons (.mcc-btn-prev / .mcc-btn-next) are rendered by PHP only
 * in slider mode and are wired up as Swiper's custom navigation elements.
 */

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css/bundle';

/**
 * Initialise all `.mcc-wrap--slider` blocks on the page.
 *
 * @returns {void}
 */
export const initMultiColumnCards = () => {
	const sliderWraps = document.querySelectorAll('.mcc-wrap--slider');
	if (!sliderWraps.length) return;

	sliderWraps.forEach((wrap) => {
		const swiperEl = wrap.querySelector('.mcc-swiper');
		const btnPrev = wrap.querySelector('.mcc-btn-prev');
		const btnNext = wrap.querySelector('.mcc-btn-next');
		const slidesPerView = parseInt(wrap.dataset.slidesPerView, 10) || 3;

		if (!swiperEl) return;

		new Swiper(swiperEl, {
			modules: [Navigation],

			// Show one card-width's worth of the next slide as a peek.
			slidesPerView: 1,
			spaceBetween: 24,
			autoHeight: true,

			// Custom nav buttons rendered by PHP.
			navigation: {
				prevEl: btnPrev,
				nextEl: btnNext,
			},

			// Keyboard navigation (accessibility).
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},

			// a11y: announce slide changes to screen readers.
			a11y: {
				enabled: true,
				prevSlideMessage: 'Previous card',
				nextSlideMessage: 'Next card',
			},

			breakpoints: {
				// >= 768px: show 2 cards.
				768: {
					slidesPerView: 2,
					spaceBetween: 24,
				},
				// >= 1024px: show the configured number (3 or 4).
				1200: {
					slidesPerView: slidesPerView,
					spaceBetween: 28,
				},
			},
		});
	});
};

// Self-initialise on DOMContentLoaded or immediately if DOM is already ready.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMultiColumnCards);
} else {
	initMultiColumnCards();
}

// ACF Block Preview support in the Gutenberg editor.
if (typeof window !== 'undefined' && window.acf) {
	window.acf.addAction(
		'render_block_preview/type=multi-column-cards',
		initMultiColumnCards
	);
}

export default initMultiColumnCards;